import React, { useCallback, useEffect, useMemo } from "react";
import { Box, Card, CardContent, Grid, Typography, IconButton, Tooltip, Menu, MenuItem, List, ListItemButton, ListItemText, ListItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import axiosBase from "../../common/axiosBase";
import coreConfig from "../../common/coreConfig";
import { logger } from "../../App";

function EquipmentCard({ item, navigate }) {
    const { apiConfig } = coreConfig;

    const [state, setState] = React.useState({
        anchorEl: null,
        nestedMenuAnchor: null,
        revisions: [],
        loading: false
    });

    const fetchRevisions = async () => {
        try {
            const response = await axiosBase({
                method: apiConfig.reqMethod.POST,
                api: apiConfig.endpoints.revision + "/list",
                body: {
                    sort: 'RevisionStatusId asc',
                    where: [{
                        field: 'EquipmentId',
                        value: item.EquipmentId,
                        operator: 'equals',
                        type: "number"
                    }]
                }
            });
            if (!response.data.success) {
                return;
            }
            const { records = [] } = response.data || {};
            setState((prevState) => ({
                ...prevState,
                revisions: records,
                loading: false
            }));
        } catch (error) {
            logger.error({ error, message: "Error fetching revisions" });
        }
    };

    const handleMenuOpen = (event) => setState((prevState) => ({ ...prevState, anchorEl: event.currentTarget }));
    const handleMenuClose = () => setState((prevState) => ({ ...prevState, anchorEl: null, nestedMenuAnchor: null }));

    const handleNestedMenuOpen = (event) => setState((prevState) => ({ ...prevState, nestedMenuAnchor: event.currentTarget }));
    const handleNestedMenuClose = () => setState((prevState) => ({ ...prevState, nestedMenuAnchor: null }));

    useEffect(() => {
        if (item.EquipmentId && state.anchorEl) {
            fetchRevisions();
        }
    }, [item.EquipmentId, state.anchorEl]);

    const equipmentDetails = useMemo(() => {
        return [
            { label: "Model", value: item.ModelName },
            { label: "Type", value: item.EquipmentType },
            { label: "Family", value: item.EquipmentFamily }
        ];
    }, [item]);

    const AnchoreIcon = useMemo(() => state.nestedMenuAnchor ? <ArrowDropDownIcon /> : <ArrowRightIcon />, [state.nestedMenuAnchor]);
    const canEditRevision = useMemo(() => item.RevisionStatus === coreConfig.draft && item.canEdit, [item]);
    const locateRevisionStatus = useCallback(() =>  canEditRevision && navigate(`/validatescope/${item.EquipmentId}?edit=true`), [item.EquipmentId, navigate, canEditRevision]);
    const nestedMenuAnchorElement = useMemo(() => state.nestedMenuAnchor ? state.nestedMenuAnchor.getBoundingClientRect() : null, [state.nestedMenuAnchor]);
    return (
        <Card variant="outlined" className="equipmentCard">
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={1}>
                <Typography variant="body1" fontWeight="bold">
                    Equipment:
                </Typography>
                <IconButton title="Menu" size="small" color="primary" onClick={handleMenuOpen}>
                    <MoreVertIcon />
                </IconButton>
            </Box>
            <Grid container justifyContent="space-around">
                <Grid size={{ xs: 6 }} pl={5} display={"flex"} flexDirection={"column"}>
                    {equipmentDetails.map(({ label, value }) =>
                        <Box display="grid" gridTemplateColumns="60px auto" key={label}>
                            <Typography fontWeight="bold">{`${label}:`}</Typography>
                            <Tooltip title={value} placement="top">
                                <Typography className="textWrap">{value}</Typography>
                            </Tooltip>
                        </Box>
                    )}
                </Grid>
                <Grid size={{ xs: 6 }} display="flex" justifyContent="center" alignItems="center">
                    <img
                        src={`/${item.EquipmentTypeImage}`}
                        alt="Equipment"
                        style={{ height: 100, objectFit: "contain" }}
                    />
                </Grid>
            </Grid>
            <CardContent>
                <Box px={2} display="grid" gridTemplateColumns="130px auto">
                    <Typography fontWeight="bold">Current Revision:</Typography>
                    {item.Revision &&
                        <Box pl={1} display="flex" onClick={() => navigate(`/validatescope/${item.EquipmentId}`)}>
                            <Typography color="primary" fontSize="medium" sx={{ cursor: "pointer" }}>
                                {item.Revision}
                            </Typography>
                            {item.ProductionVersion && (
                                <Tooltip title={item.ProductionVersion} arrow display={"flex"}>
                                    <Box component={"span"}>
                                        (
                                        <Typography className="textWrap textWrapVersion">
                                            {item.ProductionVersion}
                                        </Typography>
                                        )
                                    </Box>
                                </Tooltip>
                            )}
                            <Tooltip title="View">
                                <VisibilityIcon sx={{ pl: 1 }} color="primary" fontSize="medium" cursor="pointer" />
                            </Tooltip>
                            
                        </Box>
                    }
                </Box>
                <Box px={2} display="grid" gridTemplateColumns="130px auto">
                    <Typography fontWeight="bold">Staging Status:</Typography>
                    <Box>
                        {item.RevisionStatus &&
                            <Box pl={1}
                                onClick={locateRevisionStatus}
                                display={"flex"}
                            >
                                <Typography
                                    color={ canEditRevision ? "primary" : "inherit"}
                                    sx={{ cursor: canEditRevision ? "pointer" : "default" }}
                                >
                                    {item.RevisionStatus}
                                </Typography>
                                {item.StagedVersion && (
                                    <Tooltip title={item.StagedVersion} arrow display={"flex"}>
                                        <Box component={"span"}>
                                            (
                                            <Typography className="textWrap textWrapVersion">{item.StagedVersion}</Typography>
                                            )
                                        </Box>
                                    </Tooltip>
                                )}
                                { canEditRevision && (
                                    <Tooltip title="Edit">
                                        <EditIcon sx={{ pl: 1 }} color="primary" fontSize="medium" cursor="pointer" />
                                    </Tooltip>
                                )}
                            </Box>
                        }
                    </Box>
                </Box>
            </CardContent>
            <Menu
                anchorEl={state.anchorEl}
                open={Boolean(state.anchorEl)}
                onClose={handleMenuClose}
                disableScrollLock
            >
                <MenuItem onClick={() => navigate(`/release/${item.EquipmentId}`)}>Show Releases</MenuItem>
                <MenuItem onClick={handleNestedMenuOpen}>
                    Archives
                    {state.revisions.length ? AnchoreIcon : <></>}
                </MenuItem>
            </Menu>
            <Menu
                anchorEl={state.nestedMenuAnchor}
                open={Boolean(state.nestedMenuAnchor) && state.revisions.length}
                onClose={handleNestedMenuClose}
                disableScrollLock
                anchorReference="anchorPosition"
                anchorPosition={state.nestedMenuAnchor ? {
                    top: nestedMenuAnchorElement.bottom,
                    left: nestedMenuAnchorElement.left
                } : {
                    top: 0,
                    left: 0
                }}
            >
                <List style={{ maxHeight: 200 }} disablePadding dense>
                    {state.revisions.map(({ RevisionId, Revision, RevisionStatus }) => (<ListItem key={RevisionId} disablePadding>
                        <ListItemButton onClick={() => navigate(`/archive/${RevisionId}`)}>
                            <ListItemText primary={RevisionStatus + ' - ' + Revision} />
                        </ListItemButton>
                    </ListItem>
                    ))}
                </List>
            </Menu>
        </Card>
    );
}

export default EquipmentCard;
